/* A TRIP — the thing this app has never had.
 *
 * ═══ WHAT WAS MISSING ═══
 *
 * Three stores already describe a route and not one of them outlives the page:
 * `routePlan` holds the two points you tapped, `autoroute` holds the solve, and
 * `editor` holds the draft you are dragging. Reload and all three are gone. The
 * only way a route has ever left this chart is `downloadRouteGpx`, which is a
 * file you then have to find again.
 *
 * Every tool this competes with saves plans, because a passage is planned days
 * before it is sailed and revised several times in between. "Nanaimo to
 * Montague, leaving Sunday, 6.5 knots" is a document, not a gesture.
 *
 * ═══ WHAT A TRIP IS, AND WHAT IT DELIBERATELY IS NOT ═══
 *
 * It is the QUESTION, not the answer: the waypoints, the departure, the boat's
 * speed and draft, whether to use currents. The solved route, the schedule and
 * the holds are all recomputed from these, and are never stored.
 *
 * That is the important line. A schedule states a slack to the minute, and a
 * stored one goes stale silently — the CHS predictions get corrected, the boat
 * speed changes, the departure moves. Re-solving on load costs one worker
 * round-trip and cannot be quietly wrong. A stored answer is a claim with a
 * timestamp nobody looks at.
 *
 * ═══ CORRUPT STORAGE MUST NOT TAKE THE APP DOWN ═══
 *
 * Everything read back is validated field by field. This runs on a boat, on a
 * pi that has been in 5 V under-voltage for months, and the failure mode of an
 * unguarded `JSON.parse` at module load is a white screen before first paint —
 * which is exactly what an unguarded `crypto.randomUUID` did here once. A trip
 * that will not parse is dropped and the rest load.
 */
import { randomUUID } from './uuid';

export interface TripPoint {
  lat: number;
  lon: number;
  /** Only where somebody named it. Unnamed points are "WP 3" at display time
   *  rather than being given a name nobody chose. */
  name?: string;
}

export interface Trip {
  id: string;
  name: string;
  points: TripPoint[];
  /** Epoch ms, or null for "leave now".
   *
   *  Null is not a missing value — it is a plan that has not committed to a
   *  time, and re-reading the clock at each solve is what stops a plan made at
   *  09:00 and opened at 11:00 quietly still being a plan for 09:00. */
  departT: number | null;
  boatKn: number;
  draftM: number;
  useCurrents: boolean;
  /** Free text. The one place a skipper can write "call the marina first". */
  notes: string;
  createdAt: number;
  updatedAt: number;
}

const KEY = 'gd.trips';
/** Bumped when the stored shape changes incompatibly. On a mismatch the store
 *  starts empty rather than guessing — a trip is cheap to redraw and a
 *  half-migrated one is a plan that is wrong in a way nobody can see. */
const VERSION = 1;

export const DEFAULT_BOAT_KN = 6;
export const DEFAULT_DRAFT_M = 2;
/** Two waypoints is the least that is a passage. Below it there is nothing to
 *  route between and nothing to schedule. */
export const MIN_POINTS = 2;

interface Stored {
  version: number;
  trips: unknown[];
}

const isFiniteNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function validPoint(v: unknown): TripPoint | null {
  if (typeof v !== 'object' || v === null) return null;
  const p = v as Record<string, unknown>;
  if (!isFiniteNum(p.lat) || !isFiniteNum(p.lon)) return null;
  /* Real coordinates only. A NaN that survived JSON as a string, or a longitude
   * of 900, draws a route line to somewhere the projection cannot put it. */
  if (p.lat < -90 || p.lat > 90 || p.lon < -180 || p.lon > 180) return null;
  const name = typeof p.name === 'string' && p.name.trim() ? p.name.trim() : undefined;
  return name === undefined ? { lat: p.lat, lon: p.lon } : { lat: p.lat, lon: p.lon, name };
}

/** Parse one stored trip, or null if it is not one. Exported for the tests,
 *  which is the only way to check the tolerant path without writing rubbish
 *  into a real localStorage. */
export function parseTrip(v: unknown): Trip | null {
  if (typeof v !== 'object' || v === null) return null;
  const t = v as Record<string, unknown>;
  if (typeof t.id !== 'string' || !t.id) return null;
  if (!Array.isArray(t.points)) return null;

  const points = t.points.map(validPoint).filter((p): p is TripPoint => p !== null);
  if (points.length < MIN_POINTS) return null;

  const now = Date.now();
  return {
    id: t.id,
    name: typeof t.name === 'string' && t.name.trim() ? t.name.trim() : 'Untitled trip',
    points,
    /* A departure in the far past is not wrong — you may be looking at last
     * week's passage — so it is kept as written. Only a non-number is
     * rejected. */
    departT: isFiniteNum(t.departT) ? t.departT : null,
    boatKn: isFiniteNum(t.boatKn) && t.boatKn > 0 ? t.boatKn : DEFAULT_BOAT_KN,
    draftM: isFiniteNum(t.draftM) && t.draftM >= 0 ? t.draftM : DEFAULT_DRAFT_M,
    useCurrents: t.useCurrents === true,
    notes: typeof t.notes === 'string' ? t.notes : '',
    createdAt: isFiniteNum(t.createdAt) ? t.createdAt : now,
    updatedAt: isFiniteNum(t.updatedAt) ? t.updatedAt : now
  };
}

function read(): Trip[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Stored;
    if (typeof parsed !== 'object' || parsed === null) return [];
    if (parsed.version !== VERSION) return [];
    if (!Array.isArray(parsed.trips)) return [];
    return parsed.trips.map(parseTrip).filter((t): t is Trip => t !== null);
  } catch {
    /* Unreadable storage, a quota error, or a private-mode browser that throws
     * on access. None of them is a reason not to draw a chart. */
    return [];
  }
}

/** A name that does not collide with the ones already there — "Copy", then
 *  "Copy 2". Silent renaming is worse than a number: two trips with the same
 *  name in a list is a choice you cannot make. */
export function uniqueName(base: string, taken: readonly string[]): string {
  if (!taken.includes(base)) return base;
  for (let i = 2; ; i++) {
    const candidate = `${base} ${i}`;
    if (!taken.includes(candidate)) return candidate;
  }
}

class Trips {
  #list = $state.raw<Trip[]>([]);
  /** The trip being planned, or null. Not persisted: which one you had open is
   *  a fact about a session, and reopening a plotter into a half-finished plan
   *  from last week is worse than opening onto the chart. */
  activeId = $state<string | null>(null);

  constructor() {
    this.#list = read();
  }

  /** Newest first — the one you were working on is the one you want. */
  get list(): readonly Trip[] {
    return this.#list;
  }

  get active(): Trip | null {
    return this.#list.find((t) => t.id === this.activeId) ?? null;
  }

  get(id: string): Trip | null {
    return this.#list.find((t) => t.id === id) ?? null;
  }

  #write(next: Trip[]): void {
    this.#list = [...next].sort((a, b) => b.updatedAt - a.updatedAt);
    try {
      localStorage.setItem(KEY, JSON.stringify({ version: VERSION, trips: this.#list }));
    } catch {
      /* Full, or blocked. The in-memory list is still correct for this session,
       * and refusing to plan because a plan cannot be saved is the wrong trade
       * on a boat. */
    }
  }

  /** Save a new trip and make it active. Returns it, so a caller can name it. */
  create(init: Partial<Trip> & { points: TripPoint[] }): Trip {
    const now = Date.now();
    const trip: Trip = {
      id: randomUUID(),
      name: uniqueName(
        init.name?.trim() || 'Untitled trip',
        this.#list.map((t) => t.name)
      ),
      points: init.points,
      departT: init.departT ?? null,
      boatKn: init.boatKn ?? DEFAULT_BOAT_KN,
      draftM: init.draftM ?? DEFAULT_DRAFT_M,
      useCurrents: init.useCurrents ?? false,
      notes: init.notes ?? '',
      createdAt: now,
      updatedAt: now
    };
    this.#write([...this.#list, trip]);
    this.activeId = trip.id;
    return trip;
  }

  /** Change one, touching `updatedAt`. A no-op for an id that is not there —
   *  the alternative is throwing at a caller that has just had its trip deleted
   *  in another tab. */
  update(id: string, patch: Partial<Omit<Trip, 'id' | 'createdAt'>>): void {
    const i = this.#list.findIndex((t) => t.id === id);
    if (i === -1) return;
    const next = [...this.#list];
    next[i] = { ...next[i]!, ...patch, updatedAt: Date.now() };
    this.#write(next);
  }

  duplicate(id: string): Trip | null {
    const src = this.get(id);
    if (!src) return null;
    /* The points are copied, not shared. `$state.raw` does not clone, so a
     * duplicate that referenced the same array would move both trips' route
     * every time either was edited. */
    return this.create({
      ...src,
      name: uniqueName(`${src.name} copy`, this.#list.map((t) => t.name)),
      points: src.points.map((p) => ({ ...p }))
    });
  }

  remove(id: string): void {
    this.#write(this.#list.filter((t) => t.id !== id));
    if (this.activeId === id) this.activeId = null;
  }

  /** For the tests, and for a "clear saved plans" control if one is ever
   *  wanted. Deliberately not exposed in the UI: there is no undo. */
  clear(): void {
    this.#write([]);
    this.activeId = null;
  }
}

export const trips = new Trips();
